import logger from '@finn-no/fiaas-logger';
import { trackPageView } from '@finn-no/pulse-sdk';
import { Box, Breadcrumbs } from '@warp-ds/react';
import type { GetServerSideProps, NextPage } from 'next';
import { useEffect } from 'react';
import { ConfigProvider } from 'shared/config/ConfigContext';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { Menu } from 'shared/page/Menu';
import { config, feConfig } from '@/config';
import type { CurrencyData } from '@/interfaces';
import LandingScene from '@/scenes/landing/Landing';
import { runAllMiddlewares } from '@/server/middleware';

type CurrencyProps = { currencies: CurrencyData; config: Record<string, string> };

const CurrencyPage: NextPage<CurrencyProps> = ({ config, currencies }) => {
    useEffect(() => {
        // Pulse tracker id: TR_pv06 (see tracking section in README.md)
        trackPageView({
            object: {
                url: 'https://www.finn.no/reise/valuta/',
                type: 'Page',
                pageType: 'tool',
            },
            vertical: {
                name: 'travel',
            },
        });
    }, []);

    const description =
        'Bruk vår enkle valutakalkulator til å undersøke valutakursen før du reiser.' +
        'Sjekk kursen på dollar, pund, euro, baht og mange andre valutaer.';

    return (
        <>
            <SimpleHead
                title="Hva er valutakursen? | Valutakalkulator | FINN reise"
                canonical="https://www.finn.no/reise/valuta/"
                description={description}
            />

            <ConfigProvider config={config}>
                <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/valuta">
                    <Breadcrumbs className="px-0 sm:px-16 pt-16">
                        <a href="/reise/">Reise</a>
                        <a href="/reise/valuta/">Valuta</a>
                    </Breadcrumbs>
                    <Menu />
                    <Box bleed>
                        <LandingScene currencies={currencies} />
                    </Box>
                </Main>
            </ConfigProvider>
        </>
    );
};

export const getServerSideProps: GetServerSideProps<CurrencyProps> = async ({ req, res, query }) => {
    await runAllMiddlewares(req, res, query);

    const currencies = await fetch(config.get('currencyApiUrl'))
        .then((cur) => cur.json())
        .catch((err) => {
            logger.error(err);
            return {};
        });

    return { props: { config: feConfig, currencies } };
};

export default CurrencyPage;
